// Site-wide CMS slots for the "Read on" cards that close every public marketing
// page. Concatenated into CMS_SLOTS via ./index.
//
// WHY THIS EXISTS
// ---------------
// Each marketing page used to declare its OWN title + description for every
// card it linked to — about.next_steps_tile1_title, faq.nextsteps_tile1_title,
// notes_from_the_road.next_tile1_title, and so on, across five different key
// namings. The same destination therefore carried two, three or four different
// descriptions depending on which page you happened to be reading, and an admin
// who wanted to reword one card had to find and edit it on every page that
// pointed at it. Feedback 2026-07-24 ("the READ ON cards are the old ones …
// are these globally editable now?") is exactly that gap: the page names had
// been updated in the nav but the cards still carried the pre-rename wording,
// in eleven separate places.
//
// Now there is ONE card per destination, defined here and shared site-wide.
// Editing "Our Ethos" in the admin's Read on cards tab changes it everywhere it
// appears. The per-page slots are marked deprecated (append-only registry —
// never delete) so any existing override row doesn't orphan.
//
// Which cards a given page shows, and the order, stays in that page's component
// — the destinations are a curated editorial choice per page, not copy. Only
// the card's title and description are editable here.
//
// Titles track the live public PAGE names: How It Works, Our Ethos, Founding
// Venues, Ripples, Our Story. Non-nav destinations keep their descriptive
// names. (Our Ethos and Ripples came off the top nav on 2026-08-18 but the
// pages, and these cards, are unchanged — the cards are now the main way a
// reader is signposted to either one.)
import type { SlotDef } from '../slots';

export const nextStepsExtra: SlotDef[] = [
  // ─── Nav-facing destinations ────────────────────────────────────────────
  {
    key: 'next_steps.how_it_works_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'How It Works — card title',
    helpText: 'Shown wherever a page links on to How It Works. Links to /how-it-works.',
    default: 'How It Works',
  },
  {
    key: 'next_steps.how_it_works_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'How It Works — card description',
    default: 'The flow from search to stay, end to end, both sides.',
  },
  // The key stays `promise_*` (append-only registry; prod override rows are
  // keyed on it) but the page it points at was renamed to "Our Ethos" on
  // 2026-08-18. The URL /what-we-are-and-arent did not change.
  {
    key: 'next_steps.promise_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Our Ethos — card title',
    helpText: 'Shown wherever a page links on to Our Ethos. Links to /what-we-are-and-arent.',
    default: 'Our Ethos',
  },
  {
    key: 'next_steps.promise_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Our Ethos — card description',
    default: 'No cuts, no paid placement, no data sales. What we will never do.',
  },
  {
    key: 'next_steps.founding_venues_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Founding Venues — card title',
    helpText: 'Shown wherever a page links on to Founding Venues. Links to /founding-venue-offer.',
    default: 'Founding Venues',
  },
  {
    key: 'next_steps.founding_venues_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Founding Venues — card description',
    default: 'The offer to the first 50 venues. Same price for life.',
  },
  {
    key: 'next_steps.ripples_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Ripples — card title',
    helpText: 'Shown wherever a page links on to Ripples. Links to /ripples.',
    default: 'Ripples',
  },
  {
    key: 'next_steps.ripples_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Ripples — card description',
    // The page was renamed from Notes from the road to Ripples in July 2026;
    // this description carried the old name until 2026-08-18.
    default: 'Founder letters and replies to your feedback, as WWAAVE gets built.',
  },
  {
    key: 'next_steps.about_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Our Story — card title',
    helpText: 'Shown wherever a page links on to Our Story. Links to /about.',
    default: 'Our Story',
  },
  {
    key: 'next_steps.about_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Our Story — card description',
    default: 'Founded by a muso, not a tech bro. Where the platform came from.',
  },

  // ─── Non-nav destinations ───────────────────────────────────────────────
  {
    key: 'next_steps.faq_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'FAQ — card title',
    helpText: 'Shown wherever a page links on to the FAQ. Links to /faq.',
    default: 'FAQ',
  },
  {
    key: 'next_steps.faq_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'FAQ — card description',
    default: 'The questions we hear most: free?, verification, disputes, reviews.',
  },
  {
    key: 'next_steps.community_guidelines_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Community guidelines — card title',
    helpText:
      'Shown wherever a page links on to the community guidelines. Links to /community-guidelines.',
    default: 'How we hold the line',
  },
  {
    key: 'next_steps.community_guidelines_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Community guidelines — card description',
    default: 'Community standards and how we keep the network honest.',
  },
  {
    key: 'next_steps.dispute_resolution_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Dispute resolution — card title',
    helpText:
      'Shown wherever a page links on to dispute resolution. Links to /dispute-resolution.',
    default: 'Dispute resolution',
  },
  {
    key: 'next_steps.dispute_resolution_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Dispute resolution — card description',
    default: 'How we handle exchanges that go wrong: categories, steps, principles.',
  },
  {
    key: 'next_steps.privacy_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Privacy Policy — card title',
    helpText: 'Shown wherever a page links on to the privacy policy. Links to /privacy.',
    default: 'Privacy Policy',
  },
  {
    key: 'next_steps.privacy_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Privacy Policy — card description',
    default: 'What we collect, what we never do with it, your rights.',
  },
  {
    key: 'next_steps.terms_title',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Terms of service — card title',
    helpText: 'Shown wherever a page links on to the terms. Links to /terms.',
    default: 'Terms of service',
  },
  {
    key: 'next_steps.terms_desc',
    page: 'next_steps',
    kind: 'markdown',
    label: 'Terms of service — card description',
    default: 'Eligibility, subscriptions, content, conduct, governing law.',
  },
];
